/**
 * Service layer per le Newsletter (Sprint 18).
 * Incapsula le chiamate REST verso il backend (prefisso /api già nell'istanza axios).
 * Ogni metodo ritorna la Promise di axios; la gestione .then/.catch resta nelle pagine.
 *
 * Riferimento contratti: TDD Sprint 18 §4.2 / §11.
 */
import API from '../../api';

/** Categorie destinatari rapide ammesse (§11.3). NB: 'blocked' NON è ammessa. */
export const RECIPIENT_CATEGORIES = [
  'all',
  'member',
  'expired',
  'internal',
  'external',
  'verified',
  'unverified',
  'online',
  'offline',
];

/** Etichette IT per le categorie rapide. */
export const RECIPIENT_CATEGORY_LABELS = {
  all: 'Tutti',
  member: 'Tesserati',
  expired: 'Tesseramento scaduto',
  internal: 'Interni',
  external: 'Esterni',
  verified: 'Verificati',
  unverified: 'Non verificati',
  online: 'Registrati online',
  offline: 'Registrati offline',
};

export const newslettersApi = {
  /** GET /newsletters — lista paginata (params: page, status=<bucket>, entity_id, date_from, date_to, search). */
  list: (params) => API.get('/newsletters', { params }),

  /**
   * GET /newsletters/status-counts — conteggi per bucket per i badge dei tab.
   * → { all, draft, scheduled, sending, sent, terminated }
   * Applica lo stesso scope/filtri della lista (entity_id, date_from, date_to, search) tranne status.
   */
  statusCounts: (params) => API.get('/newsletters/status-counts', { params }),

  /**
   * GET /newsletters/report — report aggregato sulle entità autorizzate NEWSLETTER.
   * Params opzionali: entity_id, date_from, date_to (default: ultimi 12 mesi).
   * → { range, totals, by_month[], by_entity[], top_newsletters[] }
   */
  report: (params) => API.get('/newsletters/report', { params }),

  /**
   * GET /newsletters/report/entities — entità mittenti proponibili nel filtro del report.
   * Solo quelle con invii nel periodo, filtrabili per nome (params: search, date_from, date_to).
   * L'elenco è limitato lato BE: va usato come sorgente di un autocomplete, non di un select.
   */
  reportEntities: (params) => API.get('/newsletters/report/entities', { params }),

  /** GET /newsletters/{id} — dettaglio (include `metrics`). */
  get: (id) => API.get(`/newsletters/${id}`),

  /**
   * POST /newsletters — crea la newsletter.
   * Se il payload contiene `scheduled_at` il BE la crea in SCHEDULED, altrimenti DRAFT.
   */
  create: (payload) => API.post('/newsletters', payload),

  /** PUT /newsletters/{id} — modifica (solo DRAFT/SCHEDULED, else 409). */
  update: (id, payload) => API.put(`/newsletters/${id}`, payload),

  /** DELETE /newsletters/{id} — elimina (solo DRAFT/SCHEDULED). */
  remove: (id) => API.delete(`/newsletters/${id}`),

  /** POST /newsletters/{id}/schedule — programma l'invio. */
  schedule: (id, scheduledAt) => API.post(`/newsletters/${id}/schedule`, { scheduled_at: scheduledAt }),

  /** POST /newsletters/{id}/cancel — annulla una programmata/bozza. */
  cancel: (id) => API.post(`/newsletters/${id}/cancel`),

  /** POST /newsletters/{id}/send-now — invia immediatamente. */
  sendNow: (id) => API.post(`/newsletters/${id}/send-now`),

  /** POST /newsletters/{id}/stop — interrompe un invio in corso (→ STOPPED). */
  stop: (id) => API.post(`/newsletters/${id}/stop`),

  /** GET /newsletters/{id}/progress — { status, included, sent, percent }. */
  progress: (id) => API.get(`/newsletters/${id}/progress`),

  /** POST /newsletters/{id}/test-send — email di test (non incide su metriche/stato). */
  testSend: (id, testAddress) => API.post(`/newsletters/${id}/test-send`, { test_address: testAddress }),

  /** GET /newsletters/{id}/preview — { html } con placeholder NON risolti. */
  preview: (id) => API.get(`/newsletters/${id}/preview`),

  /**
   * POST /newsletters/preview — anteprima di una newsletter non ancora salvata, composta
   * come all'invio (cornice + template + contenuto).
   * payload: { entity_id, template_id?, subject?, body_html? } → { html }
   */
  previewDraft: (payload) => API.post('/newsletters/preview', payload),

  /**
   * POST /newsletters/recipients-preview — conteggio destinatari effettivi.
   * payload: { entity_id, filter_id | recipient_category }
   * → { included, excluded_global_optout, excluded_entity_blocked, excluded_no_email }
   */
  recipientsPreview: (payload) => API.post('/newsletters/recipients-preview', payload),

  /** POST /newsletters/{id}/attachments — upload allegato (multipart). */
  addAttachment: (id, formData) =>
    API.post(`/newsletters/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  /** DELETE /newsletters/{id}/attachments/{attId} — rimuove l'allegato solo da questa newsletter. */
  removeAttachment: (id, attId) => API.delete(`/newsletters/${id}/attachments/${attId}`),
};

export default newslettersApi;